import React, { useState } from "react";
import ReactTable from 'react-table';
import 'react-table/react-table.css';
import matchSorter from 'match-sorter';
import ReactExport from "react-data-export";
import moment from "moment";
import {
  BarChart,
  PieChart,
  Bar,
  Pie,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from 'recharts';
import { BotonExcel } from "./../Plantillas/Botones";

const ExcelFile = ReactExport.ExcelFile;
const ExcelSheet = ReactExport.ExcelFile.ExcelSheet;

const COLORS = ['#01a59c', '#35605a', '#ffc107', '#28a745', '#dc3545'];

const TablaEstadisticas = ({ reporteData, fechaInicio, fechaFin }) => {
    const [graficosExpandido, setGraficosExpandido] = useState(false);
    if (!reporteData) {
        return (
            <div className="alert alert-warning mt-3 shadow-sm border-0">
                <i className="fas fa-exclamation-triangle mr-2"></i>
                No hay datos de estadísticas disponibles.
            </div>
        );
    }

    const {
        despachadoresEficientes = [],
        productosMasVendidos = [],
        clientesFrecuentes = [],
        vendedoresEficientes = [],
        tiemposAusencia = []
    } = reporteData;

    const primaryColor = '#35605a';
    const secondaryColor = '#01a59c';
    const lightColor = 'rgba(53, 96, 90, 0.08)';

    // KPIs Generales
    const mejorDespachador = despachadoresEficientes.length > 0 ? despachadoresEficientes[0] : null;
    const productoEstrella = productosMasVendidos.length > 0 ? productosMasVendidos[0] : null;
    const mejorCliente = clientesFrecuentes.length > 0 ? clientesFrecuentes[0] : null;
    const mejorVendedor = vendedoresEficientes.length > 0 ? vendedoresEficientes[0] : null;

    // ----- COLUMNAS REACT-TABLE -----
    const colsDespachadores = [
        {
            Header: "Despachador",
            accessor: "NombreDespachador",
            filterMethod: (filter, rows) => matchSorter(rows, filter.value, { keys: ["NombreDespachador"] }),
            filterAll: true
        },
        {
            Header: "Pedidos Entregados",
            accessor: "TotalPedidosEntregados",
            width: 150,
            style: { textAlign: 'center' }
        },
        {
            Header: "Tiempo Promedio (Min)",
            accessor: "TiempoPromedioMinutos",
            width: 200,
            style: { textAlign: 'center' },
            Cell: ({ value }) => (
                <span className="badge badge-info" style={{ fontSize: '0.9em' }}>
                    <i className="fas fa-stopwatch mr-1"></i> {value} min
                </span>
            )
        }
    ];

    const colsProductos = [
        {
            Header: "Producto",
            accessor: "NombreProducto",
            filterMethod: (filter, rows) => matchSorter(rows, filter.value, { keys: ["NombreProducto"] }),
            filterAll: true
        },
        {
            Header: "Veces Despachado",
            accessor: "VecesDespachado",
            width: 150,
            style: { textAlign: 'center' }
        },
        {
            Header: "Cantidad Total",
            accessor: "CantidadTotalDespachada",
            width: 150,
            style: { textAlign: 'center' },
            Cell: ({ value }) => <strong>{value}</strong>
        }
    ];

    const colsClientes = [
        {
            Header: "Cliente / Razón Social",
            accessor: "NombreCliente",
            filterMethod: (filter, rows) => matchSorter(rows, filter.value, { keys: ["NombreCliente"] }),
            filterAll: true
        },
        {
            Header: "Visitas / Entregas",
            accessor: "CantidadVisitasOEntregas",
            width: 150,
            style: { textAlign: 'center' }
        },
        {
            Header: "Inversión Total",
            accessor: "MontoTotalInvertido",
            width: 180,
            style: { textAlign: 'right' },
            Cell: ({ value }) => <span style={{ color: primaryColor, fontWeight: 'bold' }}>S/ {parseFloat(value).toFixed(2)}</span>
        }
    ];

    const colsVendedores = [
        {
            Header: "Vendedor",
            accessor: "NombreVendedor",
            filterMethod: (filter, rows) => matchSorter(rows, filter.value, { keys: ["NombreVendedor"] }),
            filterAll: true
        },
        {
            Header: "Ventas Facturadas",
            accessor: "CantidadVentasFacturadas",
            width: 180,
            style: { textAlign: 'center' }
        },
        {
            Header: "Monto Vendido",
            accessor: "TotalDineroVendido",
            width: 180,
            style: { textAlign: 'right' },
            Cell: ({ value }) => <span style={{ color: secondaryColor, fontWeight: 'bold' }}>S/ {parseFloat(value).toFixed(2)}</span>
        }
    ];

    const colsAusencias = [
        {
            Header: "Operario",
            accessor: "NombreOperario",
            filterMethod: (filter, rows) => matchSorter(rows, filter.value, { keys: ["NombreOperario"] }),
            filterAll: true
        },
        {
            Header: "Pausas Solicitadas",
            accessor: "CantidadPausasSolicitadas",
            width: 180,
            style: { textAlign: 'center' }
        },
        {
            Header: "Total Tiempo Pausado",
            accessor: "TotalMinutosEnPausa",
            width: 200,
            style: { textAlign: 'center' },
            Cell: ({ value }) => (
                <span className="badge badge-warning text-dark" style={{ fontSize: '0.9em' }}>
                    <i className="fas fa-pause-circle mr-1"></i> {value || 0} min
                </span>
            )
        }
    ];

    // ----- LÓGICA DE EXCEL -----
    const getDataExportExcel = () => {
        // Configurador general para celdas de encabezado
        const headerStyle = {
            fill: { patternType: "solid", fgColor: { rgb: "F6F8FA" } },
            font: { bold: true }
        };

        // 1. Hoja Despachadores
        const hojaDespachadores = {
            columns: [
                { title: "Despachador", width: { wpx: 200 } },
                { title: "Pedidos Entregados", width: { wpx: 150 } },
                { title: "Tiempo Promedio (Min)", width: { wpx: 150 } }
            ],
            data: despachadoresEficientes.map(row => [
                { value: row.NombreDespachador || "", style: headerStyle },
                { value: row.TotalPedidosEntregados || 0, style: headerStyle },
                { value: row.TiempoPromedioMinutos || 0, style: headerStyle }
            ])
        };

        // 2. Hoja Productos
        const hojaProductos = {
            columns: [
                { title: "ID Producto Almacén", width: { wpx: 150 } },
                { title: "Producto", width: { wpx: 300 } },
                { title: "Veces Despachado", width: { wpx: 150 } },
                { title: "Cantidad Total", width: { wpx: 150 } }
            ],
            data: productosMasVendidos.map(row => [
                { value: row.IDProductoAlmacen || "", style: headerStyle },
                { value: row.NombreProducto || "", style: headerStyle },
                { value: row.VecesDespachado || 0, style: headerStyle },
                { value: row.CantidadTotalDespachada || 0, style: headerStyle }
            ])
        };

        // 3. Hoja Clientes
        const hojaClientes = {
            columns: [
                { title: "ID Cliente", width: { wpx: 100 } },
                { title: "Razón Social / Nombre", width: { wpx: 300 } },
                { title: "Visitas/Entregas", width: { wpx: 150 } },
                { title: "Inversión Total (S/)", width: { wpx: 150 } }
            ],
            data: clientesFrecuentes.map(row => [
                { value: row.IdCliente || "", style: headerStyle },
                { value: row.NombreCliente || "", style: headerStyle },
                { value: row.CantidadVisitasOEntregas || 0, style: headerStyle },
                { value: row.MontoTotalInvertido || 0, style: headerStyle }
            ])
        };

        // 4. Hoja Vendedores
        const hojaVendedores = {
            columns: [
                { title: "ID Vendedor", width: { wpx: 100 } },
                { title: "Vendedor", width: { wpx: 200 } },
                { title: "Ventas Facturadas", width: { wpx: 150 } },
                { title: "Monto Vendido (S/)", width: { wpx: 150 } }
            ],
            data: vendedoresEficientes.map(row => [
                { value: row.IdVendedor || "", style: headerStyle },
                { value: row.NombreVendedor || "", style: headerStyle },
                { value: row.CantidadVentasFacturadas || 0, style: headerStyle },
                { value: row.TotalDineroVendido || 0, style: headerStyle }
            ])
        };

        // 5. Hoja Ausencias
        const hojaAusencias = {
            columns: [
                { title: "ID Operario", width: { wpx: 100 } },
                { title: "Operario", width: { wpx: 200 } },
                { title: "Pausas Solicitadas", width: { wpx: 150 } },
                { title: "Total Tiempo Pausado (Min)", width: { wpx: 180 } }
            ],
            data: tiemposAusencia.map(row => [
                { value: row.IdOperario || "", style: headerStyle },
                { value: row.NombreOperario || "", style: headerStyle },
                { value: row.CantidadPausasSolicitadas || 0, style: headerStyle },
                { value: row.TotalMinutosEnPausa || 0, style: headerStyle }
            ])
        };

        return {
            hojaDespachadores: [hojaDespachadores],
            hojaProductos: [hojaProductos],
            hojaClientes: [hojaClientes],
            hojaVendedores: [hojaVendedores],
            hojaAusencias: [hojaAusencias]
        };
    };

    const exportData = getDataExportExcel();

    // Preparar Data para Gráficos (Top 5)
    const topDespachadores = despachadoresEficientes.slice(0, 5).map(d => ({
        ...d,
        TiempoPromedioMinutos: parseFloat(d.TiempoPromedioMinutos)
    }));
    const topProductos = productosMasVendidos.slice(0, 5).map(p => ({
        ...p,
        CantidadTotalDespachada: parseInt(p.CantidadTotalDespachada)
    }));
    const topVendedores = vendedoresEficientes.slice(0, 5).map(v => ({
        ...v,
        TotalDineroVendido: parseFloat(v.TotalDineroVendido)
    }));

    return (
        <div className="mt-4 animated fadeIn">

            {/* SECCIÓN EXCEL Y CABECERA */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 style={{ color: primaryColor }}>Resumen del Periodo</h5>
                <div>
                    <ExcelFile
                        element={
                            <BotonExcel
                                className="mr-2"
                                type="button"
                                title="Exportar Todo a Excel"
                            />
                        }
                        filename={`DashboardDespachos_${moment().format('YYYYMMDD_HHmmss')}`}
                    >
                        <ExcelSheet dataSet={exportData.hojaDespachadores} name="Despachadores" />
                        <ExcelSheet dataSet={exportData.hojaProductos} name="Productos" />
                        <ExcelSheet dataSet={exportData.hojaClientes} name="Clientes" />
                        <ExcelSheet dataSet={exportData.hojaVendedores} name="Vendedores" />
                        <ExcelSheet dataSet={exportData.hojaAusencias} name="Ausencias" />
                    </ExcelFile>
                </div>
            </div>

            {/* 1. TARJETAS DE RESUMEN (KPIs) */}
            <div className="row mb-4">
                <div className="col-md-3 mb-3">
                    <div className="card h-100 border-0 shadow-sm" style={{ borderTop: `4px solid ${secondaryColor}` }}>
                        <div className="card-header text-white" style={{ backgroundColor: primaryColor }}>
                            <h6 className="mb-0 text-truncate"><i className="fas fa-running mr-2"></i>Mejor Despachador</h6>
                        </div>
                        <div className="card-body">
                            {mejorDespachador ? (
                                <>
                                    <div className="h5 mb-0 font-weight-bold text-truncate" style={{ color: primaryColor }} title={mejorDespachador.NombreDespachador}>
                                        {mejorDespachador.NombreDespachador}
                                    </div>
                                    <small className="text-muted">{mejorDespachador.TiempoPromedioMinutos} min/despacho</small>
                                </>
                            ) : <small className="text-muted">Sin datos</small>}
                        </div>
                    </div>
                </div>

                <div className="col-md-3 mb-3">
                    <div className="card h-100 border-0 shadow-sm" style={{ borderTop: `4px solid ${secondaryColor}` }}>
                        <div className="card-header text-white" style={{ backgroundColor: primaryColor }}>
                            <h6 className="mb-0 text-truncate"><i className="fas fa-star mr-2"></i>Producto Estrella</h6>
                        </div>
                        <div className="card-body">
                            {productoEstrella ? (
                                <>
                                    <div className="h5 mb-0 font-weight-bold text-truncate" style={{ color: primaryColor }} title={productoEstrella.NombreProducto}>
                                        {productoEstrella.NombreProducto}
                                    </div>
                                    <small className="text-muted">{productoEstrella.CantidadTotalDespachada} unid. despachadas</small>
                                </>
                            ) : <small className="text-muted">Sin datos</small>}
                        </div>
                    </div>
                </div>

                <div className="col-md-3 mb-3">
                    <div className="card h-100 border-0 shadow-sm" style={{ borderTop: `4px solid ${secondaryColor}` }}>
                        <div className="card-header text-white" style={{ backgroundColor: primaryColor }}>
                            <h6 className="mb-0 text-truncate"><i className="fas fa-handshake mr-2"></i>Cliente Top</h6>
                        </div>
                        <div className="card-body">
                            {mejorCliente ? (
                                <>
                                    <div className="h5 mb-0 font-weight-bold text-truncate" style={{ color: primaryColor }} title={mejorCliente.NombreCliente}>
                                        {mejorCliente.NombreCliente}
                                    </div>
                                    <small className="text-muted">S/ {parseFloat(mejorCliente.MontoTotalInvertido).toFixed(2)} invertidos</small>
                                </>
                            ) : <small className="text-muted">Sin datos</small>}
                        </div>
                    </div>
                </div>

                <div className="col-md-3 mb-3">
                    <div className="card h-100 border-0 shadow-sm" style={{ borderTop: `4px solid ${secondaryColor}` }}>
                        <div className="card-header text-white" style={{ backgroundColor: primaryColor }}>
                            <h6 className="mb-0 text-truncate"><i className="fas fa-medal mr-2"></i>Mejor Vendedor</h6>
                        </div>
                        <div className="card-body">
                            {mejorVendedor ? (
                                <>
                                    <div className="h5 mb-0 font-weight-bold text-truncate" style={{ color: primaryColor }} title={mejorVendedor.NombreVendedor}>
                                        {mejorVendedor.NombreVendedor}
                                    </div>
                                    <small className="text-muted">S/ {parseFloat(mejorVendedor.TotalDineroVendido).toFixed(2)} vendidos</small>
                                </>
                            ) : <small className="text-muted">Sin datos</small>}
                        </div>
                    </div>
                </div>
            </div>

            {/* SECCIÓN: GRÁFICOS (DESPLEGABLE) */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card border-0 shadow-sm" style={{ borderTop: `4px solid ${primaryColor}` }}>
                        <div className="card-header text-white d-flex justify-content-between align-items-center" style={{ backgroundColor: primaryColor }}>
                            <div className="d-flex align-items-center">
                                <h6 className="mb-0">
                                    <i className="fas fa-chart-pie mr-2"></i>
                                    Análisis Gráfico (Top 5)
                                </h6>
                            </div>
                            <div>
                                <button 
                                    className="btn btn-sm btn-light"
                                    onClick={() => setGraficosExpandido(!graficosExpandido)}
                                >
                                    <i className={`fas fa-chevron-${graficosExpandido ? 'up' : 'down'} mr-1`}></i>
                                    {graficosExpandido ? 'Ocultar' : 'Ver Gráficos'}
                                </button>
                            </div>
                        </div>

                        {graficosExpandido && (
                            <div className="card-body bg-light">
                                <div className="row">
                                    {/* Gráfico 1: Tiempos Despachadores */}
                                    <div className="col-md-4 mb-3">
                                        <div className="card h-100 border-0 shadow-sm">
                                            <div className="card-body">
                                                <h6 className="text-center" style={{ color: primaryColor }}>Tiempos Promedio (Min)</h6>
                                                <ResponsiveContainer width="100%" height={250}>
                                                    <BarChart data={topDespachadores} layout="vertical" margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
                                                        <CartesianGrid strokeDasharray="3 3" />
                                                        <XAxis type="number" />
                                                        <YAxis dataKey="NombreDespachador" type="category" width={80} tick={{fontSize: 11}} />
                                                        <Tooltip />
                                                        <Bar dataKey="TiempoPromedioMinutos" fill={secondaryColor} name="Minutos">
                                                            {topDespachadores.map((entry, index) => (
                                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                            ))}
                                                        </Bar>
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Gráfico 2: Top Productos */}
                                    <div className="col-md-4 mb-3">
                                        <div className="card h-100 border-0 shadow-sm">
                                            <div className="card-body">
                                                <h6 className="text-center" style={{ color: primaryColor }}>Cantidades Top Productos</h6>
                                                <ResponsiveContainer width="100%" height={250}>
                                                    <BarChart data={topProductos} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                                                        <CartesianGrid strokeDasharray="3 3" />
                                                        <XAxis dataKey="NombreProducto" tickFormatter={(v) => v.substring(0, 10) + '...'} tick={{fontSize: 10}} />
                                                        <YAxis />
                                                        <Tooltip />
                                                        <Bar dataKey="CantidadTotalDespachada" fill={primaryColor} name="Unidades">
                                                            {topProductos.map((entry, index) => (
                                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                            ))}
                                                        </Bar>
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Gráfico 3: Vendedores por Monto (Pie) */}
                                    <div className="col-md-4 mb-3">
                                        <div className="card h-100 border-0 shadow-sm">
                                            <div className="card-body">
                                                <h6 className="text-center" style={{ color: primaryColor }}>Vendedores vs Monto Vendido</h6>
                                                <ResponsiveContainer width="100%" height={250}>
                                                    <PieChart>
                                                        <Pie
                                                            data={topVendedores}
                                                            cx="50%"
                                                            cy="50%"
                                                            outerRadius={70}
                                                            dataKey="TotalDineroVendido"
                                                            nameKey="NombreVendedor"
                                                            label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                                                            labelLine={false}
                                                        >
                                                            {topVendedores.map((entry, index) => (
                                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                            ))}
                                                        </Pie>
                                                        <Tooltip formatter={(value) => `S/ ${value.toFixed(2)}`} />
                                                        <Legend wrapperStyle={{fontSize: '11px'}} />
                                                    </PieChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* TABLAS DE DETALLE - ORGANIZADAS EN FILAS */}

            <div className="row mb-4">
                {/* Tabla 1: Despachadores */}
                <div className="col-lg-6 mb-3">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-header d-flex justify-content-between align-items-center" style={{ backgroundColor: lightColor }}>
                            <h6 className="mb-0" style={{ color: primaryColor }}>
                                <i className="fas fa-stopwatch mr-2"></i>Eficiencia de Despachadores
                            </h6>
                            <span className="badge badge-light text-muted">{despachadoresEficientes.length} reg.</span>
                        </div>
                        <div className="card-body p-0">
                            <ReactTable
                                data={despachadoresEficientes}
                                columns={colsDespachadores}
                                filterable={true}
                                defaultPageSize={5}
                                showPageSizeOptions={false}
                                className="-striped -highlight"
                                previousText='Ant'
                                nextText='Sig'
                                pageText='Pág'
                                ofText='de'
                                rowsText='filas'
                                noDataText='No hay registros'
                            />
                        </div>
                    </div>
                </div>

                {/* Tabla 2: Tiempos de ausencia */}
                <div className="col-lg-6 mb-3">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-header d-flex justify-content-between align-items-center" style={{ backgroundColor: lightColor }}>
                            <h6 className="mb-0" style={{ color: primaryColor }}>
                                <i className="fas fa-coffee mr-2"></i>Control de Tiempos Muertos (Pausas)
                            </h6>
                            <span className="badge badge-light text-muted">{tiemposAusencia.length} reg.</span>
                        </div>
                        <div className="card-body p-0">
                            <ReactTable
                                data={tiemposAusencia}
                                columns={colsAusencias}
                                filterable={true}
                                defaultPageSize={5}
                                showPageSizeOptions={false}
                                className="-striped -highlight"
                                previousText='Ant'
                                nextText='Sig'
                                pageText='Pág'
                                ofText='de'
                                rowsText='filas'
                                noDataText='No hay registros'
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="row mb-4">
                {/* Tabla 3: Vendedores */}
                <div className="col-lg-6 mb-3">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-header d-flex justify-content-between align-items-center" style={{ backgroundColor: lightColor }}>
                            <h6 className="mb-0" style={{ color: primaryColor }}>
                                <i className="fas fa-chart-line mr-2"></i>Rendimiento de Vendedores
                            </h6>
                            <span className="badge badge-light text-muted">{vendedoresEficientes.length} reg.</span>
                        </div>
                        <div className="card-body p-0">
                            <ReactTable
                                data={vendedoresEficientes}
                                columns={colsVendedores}
                                filterable={true}
                                defaultPageSize={5}
                                showPageSizeOptions={false}
                                className="-striped -highlight"
                                previousText='Ant'
                                nextText='Sig'
                                pageText='Pág'
                                ofText='de'
                                rowsText='filas'
                                noDataText='No hay registros'
                            />
                        </div>
                    </div>
                </div>

                {/* Tabla 4: Productos */}
                <div className="col-lg-6 mb-3">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-header d-flex justify-content-between align-items-center" style={{ backgroundColor: lightColor }}>
                            <h6 className="mb-0" style={{ color: primaryColor }}>
                                <i className="fas fa-users mr-2"></i>Clientes Frecuentes
                            </h6>
                            <span className="badge badge-light text-muted">{clientesFrecuentes.length} reg.</span>
                        </div>
                        <div className="card-body p-0">
                            <ReactTable
                                data={clientesFrecuentes}
                                columns={colsClientes}
                                filterable={true}
                                defaultPageSize={5}
                                showPageSizeOptions={false}
                                className="-striped -highlight"
                                previousText='Ant'
                                nextText='Sig'
                                pageText='Pág'
                                ofText='de'
                                rowsText='filas'
                                noDataText='No hay registros'

                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabla 5: Clientes Ancho Completo */}
            <div className="row mb-4">
                <div className="col-12 mb-3">
                    <div className="card border-0 shadow-sm">
                        <div className="card-header d-flex justify-content-between align-items-center" style={{ backgroundColor: lightColor }}>
                            <h6 className="mb-0" style={{ color: primaryColor }}>
                                <i className="fas fa-boxes mr-2"></i>Top Productos Despachados
                            </h6>
                            <span className="badge badge-light text-muted">{productosMasVendidos.length} reg.</span>
                        </div>
                        <div className="card-body p-0">
                            <ReactTable
                                data={productosMasVendidos}
                                columns={colsProductos}
                                filterable={true}
                                defaultPageSize={10}
                                className="-striped -highlight"
                                previousText='Ant'
                                nextText='Sig'
                                pageText='Pág'
                                ofText='de'
                                rowsText='filas'
                                noDataText='No hay registros'
                            />
                        </div>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default TablaEstadisticas;
